import { useEffect, useState } from 'react';
import { Modal, Radio, RadioGroup, FormControlLabel, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import CommonModalButton from '../../../form-components/CommonModalButton';
import { updateAppVersion } from '../../../api/appVersion';
import {
  ModalBox,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  FieldRow,
  FieldLabel,
  FieldHint,
} from './AppVersionManagementStyled';

const PLATFORM_LABELS = { I: 'iOS', A: 'Android' };
const APP_LABELS = { M: '회원앱', T: '강사앱', G: '매니저앱' };

const AppVersionManagementEditModal = ({ open, onClose, target }) => {
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    version: '',
    forceUpdateYn: 'N',
    checkYn: 'N',
    checkMessage: '',
  });

  // 모달이 열릴 때마다 선택된 행 값으로 초기화
  useEffect(() => {
    if (open && target) {
      setForm({
        version: target.version || '',
        forceUpdateYn: target.forceUpdateYn || 'N',
        checkYn: target.checkYn || 'N',
        checkMessage: target.checkMessage || '',
      });
    }
  }, [open, target]);

  const mutation = useMutation({
    mutationFn: payload => updateAppVersion(target.id, payload, user.token),
    onSuccess: () => {
      toast.success('저장되었습니다');
      queryClient.invalidateQueries({ queryKey: ['appVersions'] });
      onClose();
    },
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.version.trim()) {
      toast.error('최신 버전을 입력해주세요');
      return;
    }
    mutation.mutate(form);
  };

  if (!target) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalBox>
        <ModalHeader>
          <ModalTitle>
            {PLATFORM_LABELS[target.phoneType] || target.phoneType} · {APP_LABELS[target.appType] || target.appType}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <FieldRow>
            <FieldLabel>최신 버전</FieldLabel>
            <TextField
              size="small"
              value={form.version}
              onChange={e => handleChange('version', e.target.value)}
              placeholder="예: 1.2.3"
            />
          </FieldRow>

          <FieldRow>
            <FieldLabel>강제업데이트</FieldLabel>
            <RadioGroup
              row
              value={form.forceUpdateYn}
              onChange={e => handleChange('forceUpdateYn', e.target.value)}>
              <FormControlLabel value="N" control={<Radio />} label="사용 안 함" />
              <FormControlLabel value="Y" control={<Radio />} label="사용" />
            </RadioGroup>
            <FieldHint>사용으로 켜면 위 최신 버전 미만 클라이언트는 닫을 수 없는 업데이트 안내를 보게 됩니다.</FieldHint>
          </FieldRow>

          <FieldRow>
            <FieldLabel>점검 공지</FieldLabel>
            <RadioGroup row value={form.checkYn} onChange={e => handleChange('checkYn', e.target.value)}>
              <FormControlLabel value="N" control={<Radio />} label="사용 안 함" />
              <FormControlLabel value="Y" control={<Radio />} label="사용" />
            </RadioGroup>
            <TextField
              size="small"
              multiline
              minRows={2}
              value={form.checkMessage}
              onChange={e => handleChange('checkMessage', e.target.value)}
              placeholder="점검 공지가 켜져 있을 때 앱에 노출할 안내 문구"
            />
          </FieldRow>
        </ModalBody>
        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={onClose}>
            취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSubmit} disabled={mutation.isPending}>
            저장
          </CommonModalButton>
        </ModalFooter>
      </ModalBox>
    </Modal>
  );
};

export default AppVersionManagementEditModal;
